import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { LocalConfig, transformParameters } from 'utils';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input, Label, Loader, Select } from '@atlas/ui';

import PaymentListItem from 'src/components/ClientProfile/Payment/PaymentListItem';

import { showWithdrawalErrorWithSupport, submitWithdrawal } from './withdrawalUtils';

const accountNumberMinLength = 13;
const accountNumberMaxLength = 18;

const validateAccountNumber = value => {
  if (!value) return { message: 'required' };

  if (value.length < accountNumberMinLength) {
    return { message: 'min_length', params: { ruleOption: accountNumberMinLength } };
  } else if (value.length > accountNumberMaxLength) {
    return { message: 'max_length', params: { ruleOption: accountNumberMaxLength } };
  } else {
    return null;
  }
};

const WithdrawalKushkiPopup = ({ data = {}, id }) => {
  const { paymentMethod, pmId, publicPaymentId, redirectUrl } = data;

  const [loading, setLoading] = useState(true);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [fieldsData, setFieldsData] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectId = LocalConfig.get('projectId');

  const [accountNumberInput, setAccountNumberInput] = useState('');
  const [bankCodeInput, setBankCodeInput] = useState('');
  const [documentTypeInput, setDocumentTypeInput] = useState('');
  const [accountTypeInput, setAccountTypeInput] = useState('');

  const { amount, country, currency, documentNumber } = parseWithdrawalQuery(redirectUrl);

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const requestUrl = useMemo(
    () =>
      `payment/v2/payments/kushki/parameters?project-id=${projectId}&country=MX&param-names=BANK_CODE&param-names=ACCOUNT_TYPE&param-names=CURRENCY&param-names=DOC_TYPE&param-names=COUNTRY`,
    [projectId],
  );

  useEffect(() => {
    const getFieldsData = prepareRequest({
      method: 'GET',
      url: requestUrl,
    });

    setLoading(true);
    getFieldsData()
      .then(response => {
        const parsedData = transformParameters(response.paymentParametersList);
        setFieldsData(parsedData);
      })
      .catch(() => {
        showWithdrawalErrorWithSupport();
        handleClose();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [handleClose, requestUrl]);

  const changeAccountNumber = useCallback(event => {
    const value = event.target.value.replace(/(?!^\+)[^\d]/g, '');

    const error = validateAccountNumber(value);

    setError(error);
    setAccountNumberInput(value);
  }, []);

  const handleClickButton = useCallback(async () => {
    const requestData = {
      additionalParameters: {
        accountNumber: accountNumberInput,
        accountType: accountTypeInput,
        bankId: bankCodeInput,
        documentNumber: documentNumber,
        documentType: documentTypeInput,
        publicPaymentId: publicPaymentId,
      },
      amount: amount,
      currency: currency,
      pmId: pmId,
    };

    setIsSubmitting(true);

    try {
      await submitWithdrawal({ amount, currency, paymentMethod, requestData });
    } catch {
      showWithdrawalErrorWithSupport();
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  }, [
    accountNumberInput,
    accountTypeInput,
    amount,
    bankCodeInput,
    currency,
    documentNumber,
    documentTypeInput,
    handleClose,
    paymentMethod,
    pmId,
    publicPaymentId,
  ]);

  return (
    <DefaultPopup
      className={cx('payment-popup', 'full-height')}
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="payment-popup-content">
        <div className="popup-payment-method">
          <PaymentListItem amount={amount} payment={paymentMethod} showAmount />
        </div>

        {loading && <Loader className={cx('form-loader', { 'loader-long': country === 'EC' })} />}

        {!loading && (
          <>
            <div className="payment-popup-block">
              <Input
                active={isActiveInput}
                className="popup-input"
                error={error}
                inputMode="numeric"
                label="prontopaga_account_number"
                maxLength={accountNumberMaxLength}
                minLength={accountNumberMinLength}
                onBlur={() => setIsActiveInput(false)}
                onChange={changeAccountNumber}
                onFocus={() => setIsActiveInput(true)}
                type="number"
                value={accountNumberInput}
              />
              {error && (
                <div className="help-text-container">
                  <Label message={error.message} params={error.params} />
                </div>
              )}
            </div>
            {fieldsData['ACCOUNT_TYPE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_account_type'}
                className="payment-popup-select"
                label="withdrawal_account_type"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setAccountTypeInput}
                onFocus={() => setIsActiveSelect('withdrawal_account_type')}
                options={fieldsData['ACCOUNT_TYPE']}
              />
            )}
            {fieldsData['BANK_CODE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_bank_code'}
                className="payment-popup-select"
                label="withdrawal_bank_code"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setBankCodeInput}
                onFocus={() => setIsActiveSelect('withdrawal_bank_code')}
                options={fieldsData['BANK_CODE']}
              />
            )}
            {fieldsData['DOC_TYPE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_document_type'}
                className="payment-popup-select"
                label="withdrawal_document_type"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setDocumentTypeInput}
                onFocus={() => setIsActiveSelect('withdrawal_document_type')}
                options={fieldsData['DOC_TYPE']}
              />
            )}
          </>
        )}
      </div>

      {!loading && (
        <div className="popup__footer">
          <Button
            className="primary big block"
            disabled={!accountNumberInput || isSubmitting || error}
            error={error}
            label="withdrawal"
            loading={isSubmitting}
            onClick={handleClickButton}
          />
        </div>
      )}
    </DefaultPopup>
  );
};

export default WithdrawalKushkiPopup;
